
import { useEffect, useRef } from 'react';

export default function ServicesSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, observerOptions);

    const serviceCards = sectionRef.current?.querySelectorAll('.service-card');
    serviceCards?.forEach(card => observer.observe(card));

    // Parallax effect for background
    const handleScroll = () => {
      if (sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        const scrolled = (window.innerHeight - rect.top) / (window.innerHeight + rect.height);
        const parallaxSpeed = scrolled * 30;
        
        const bgElements = sectionRef.current.querySelectorAll('.parallax-bg');
        bgElements.forEach((element, index) => {
          const speed = (index + 1) * 20;
          (element as HTMLElement).style.transform = `translateY(${parallaxSpeed + speed}px) rotate(${scrolled * 45}deg)`;
        });
      }
    };

    window.addEventListener('scroll', handleScroll);
    
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const services = [
    {
      icon: 'ri-pages-line',
      title: 'ClickFunnels Development',
      description: 'Complete sales funnels built in ClickFunnels with high-converting landing pages, checkout flows, and automated sequences.',
      image: 'https://readdy.ai/api/search-image?query=ClickFunnels%20interface%20on%20laptop%20screen%20showing%20sales%20funnel%20builder%2C%20modern%20workspace%20setup%2C%20funnel%20flowchart%20graphics%2C%20professional%20marketing%20dashboard%2C%20clean%20white%20background%20with%20blue%20accents%2C%2016:9%20aspect%20ratio%20with%20Cofunnels%20watermark&width=400&height=225&seq=service-clickfunnels-001&orientation=landscape',
      features: ['Landing Pages', 'Sales Pages', 'Checkout Flow', 'Thank You Pages'],
      color: 'blue'
    },
    {
      icon: 'ri-settings-3-line',
      title: 'GoHighLevel Funnels',
      description: 'Complete marketing automation funnels in GoHighLevel with CRM integration, email sequences, and SMS campaigns.',
      image: 'https://readdy.ai/api/search-image?query=GoHighLevel%20dashboard%20interface%20showing%20marketing%20automation%2C%20CRM%20system%20on%20computer%20screen%2C%20professional%20office%20environment%2C%20funnel%20automation%20graphics%2C%20clean%20modern%20design%20with%20purple%20accents%2C%2016:9%20aspect%20ratio%20with%20Cofunnels%20watermark&width=400&height=225&seq=service-gohighlevel-001&orientation=landscape',
      features: ['CRM Integration', 'Email Automation', 'SMS Campaigns', 'Pipeline Management'],
      color: 'purple'
    },
    {
      icon: 'ri-smartphone-line',
      title: 'Mobile-Optimized Funnels',
      description: 'Responsive funnels that convert perfectly on all devices with lightning-fast loading speeds and seamless user experience.',
      image: 'https://readdy.ai/api/search-image?query=Multiple%20smartphones%20and%20tablets%20displaying%20responsive%20sales%20funnels%2C%20mobile-first%20design%20showcase%2C%20modern%20device%20mockups%2C%20clean%20white%20background%2C%20professional%20product%20photography%2C%2016:9%20aspect%20ratio%20with%20Cofunnels%20watermark&width=400&height=225&seq=service-mobile-001&orientation=landscape',
      features: ['Responsive Design', 'Fast Loading', 'Touch Optimized', 'Cross-Platform'],
      color: 'green'
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Conversion Optimization',
      description: 'A/B tested elements and conversion rate optimization to maximize your funnel performance and increase sales.',
      image: 'https://readdy.ai/api/search-image?query=Conversion%20rate%20optimization%20dashboard%20with%20A%2FB%20testing%20results%2C%20analytics%20charts%20showing%20improved%20performance%2C%20laptop%20displaying%20optimization%20metrics%2C%20professional%20workspace%2C%2016:9%20aspect%20ratio%20with%20Cofunnels%20watermark&width=400&height=225&seq=service-optimization-001&orientation=landscape',
      features: ['A/B Testing', 'Analytics Setup', 'Performance Tracking', 'ROI Optimization'],
      color: 'yellow'
    },
    {
      icon: 'ri-mail-line',
      title: 'Email Marketing Integration',
      description: 'Seamless integration with all major email platforms and automated follow-up sequences that nurture leads into customers.',
      image: 'https://readdy.ai/api/search-image?query=Email%20marketing%20automation%20interface%20showing%20drip%20campaigns%2C%20marketing%20emails%20on%20computer%20screen%2C%20professional%20office%20setup%2C%20email%20sequence%20flowchart%2C%20modern%20design%20with%20green%20accents%2C%2016:9%20aspect%20ratio%20with%20Cofunnels%20watermark&width=400&height=225&seq=service-email-001&orientation=landscape',
      features: ['Drip Campaigns', 'Segmentation', 'Personalization', 'Automation'],
      color: 'indigo'
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'Ongoing Support',
      description: '24/7 support and maintenance to ensure your funnels keep performing at their best with regular updates and optimizations.',
      image: 'https://readdy.ai/api/search-image?query=Customer%20support%20team%20helping%20clients%2C%20professional%20support%20desk%20environment%2C%20live%20chat%20interface%20on%20screens%2C%20friendly%20support%20representatives%2C%20modern%20office%20with%20headsets%2C%2016:9%20aspect%20ratio%20with%20Cofunnels%20watermark&width=400&height=225&seq=service-support-001&orientation=landscape',
      features: ['24/7 Support', 'Regular Updates', 'Performance Monitoring', 'Technical Assistance'],
      color: 'red'
    }
  ];

  return (
    <section ref={sectionRef} id="services" className="py-20 bg-gray-50 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="parallax-bg absolute -top-40 -right-40 w-80 h-80 bg-blue-100 rounded-full opacity-10"></div>
        <div className="parallax-bg absolute top-60 -left-40 w-60 h-60 bg-purple-100 rounded-full opacity-10"></div>
        <div className="parallax-bg absolute -bottom-40 right-20 w-96 h-96 bg-green-100 rounded-full opacity-10"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Our Funnel Services
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Complete funnel solutions designed to convert visitors into customers and maximize your revenue
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div 
              key={index} 
              className="service-card bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden group cursor-pointer opacity-0 translate-y-12 hover:translate-y-[-4px]"
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className="relative h-56 overflow-hidden">
                <img 
                  src={service.image} 
                  alt={service.title}
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent group-hover:from-black/40 transition-all duration-500"></div>
                <div className={`absolute top-4 right-4 w-12 h-12 bg-${service.color}-500 rounded-full flex items-center justify-center transform rotate-12 group-hover:rotate-0 group-hover:scale-110 transition-all duration-500`}>
                  <i className={`${service.icon} text-white text-xl`}></i>
                </div>
              </div>
              <div className="p-6">
                <div className={`w-12 h-12 bg-${service.color}-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-${service.color}-600 transition-all duration-500 group-hover:scale-110 group-hover:rotate-6`}>
                  <i className={`${service.icon} text-2xl text-${service.color}-600 group-hover:text-white transition-colors duration-500 group-hover:animate-pulse`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors duration-300">{service.title}</h3>
                <p className="text-gray-600 leading-relaxed mb-4">{service.description}</p>
                
                <div className="space-y-2 mb-4">
                  {service.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center text-sm text-gray-600 group-hover:text-gray-700 transition-colors duration-300">
                      <i className="ri-check-line text-green-500 mr-2 group-hover:animate-bounce" style={{ animationDelay: `${idx * 100}ms` }}></i>
                      {feature}
                    </div>
                  ))}
                </div>
                
                <div className={`flex items-center text-${service.color}-600 font-medium group-hover:text-${service.color}-700 transition-all duration-300`}>
                  Learn More <i className="ri-arrow-right-line ml-1 group-hover:translate-x-2 transition-transform duration-300"></i>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <style jsx>{`
        .animate-in {
          opacity: 1 !important;
          transform: translateY(0) !important;
        }
        .service-card {
          transition: opacity 0.8s ease-out, transform 0.8s ease-out;
        }
      `}</style>
    </section>
  );
}
